import { useEffect, useState } from 'react';
import GuardTable from '../../components/GuardTable';
import { adminApi, portalApi } from '../../services/portalApi';
import type { Guard } from '../../types';

function sortGuards(guards: Guard[]): Guard[] {
  return [...guards].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return a.display_name.localeCompare(b.display_name);
  });
}

export default function Guards() {
  const [guards, setGuards] = useState<Guard[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGuardIds, setActiveGuardIds] = useState<Set<string>>(new Set());
  const [nameFilter, setNameFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPin, setNewPin] = useState('');
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  function loadAll() {
    setLoading(true);
    Promise.all([
      adminApi.listGuards(),
      portalApi.activeSessions().catch(() => []),
    ]).then(([gs, sessions]) => {
      setGuards(gs);
      setActiveGuardIds(new Set(sessions.map((s) => s.guard_id)));
    }).finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddError('');
    if (!/^\d{4,6}$/.test(newPin)) { setAddError('PIN must be 4–6 digits.'); return; }
    setSaving(true);
    try {
      await adminApi.createGuard({ display_name: newName, pin: newPin });
      setShowAdd(false);
      setNewName('');
      setNewPin('');
      loadAll();
    } catch {
      setAddError('Failed to create patroller.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(id: string) {
    await adminApi.updateGuard(id, { is_active: false });
    setGuards((prev) => prev.map((g) => g.id === id ? { ...g, is_active: false } : g));
  }

  async function handleResetPin(id: string, pin: string) {
    await adminApi.updateGuard(id, { pin });
  }

  async function handleDelete(id: string) {
    await adminApi.deleteGuard(id);
    setGuards((prev) => prev.filter((g) => g.id !== id));
  }

  const filtered = sortGuards(
    guards.filter((g) =>
      !nameFilter || g.display_name.toLowerCase().includes(nameFilter.toLowerCase()),
    ),
  );

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Patrollers</h1>
          <p className="text-sm text-gray-500">
            {guards.length} patroller{guards.length !== 1 ? 's' : ''}
            {activeGuardIds.size > 0 && (
              <span className="ml-1 text-green-600">· {activeGuardIds.size} on patrol</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          + Add patroller
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Filter by name…"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-64"
        />
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="bg-blue-50 border border-brand-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input
              type="text"
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="John Smith"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">PIN (4–6 digits)</label>
            <input
              type="text"
              inputMode="numeric"
              required
              maxLength={6}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              placeholder="1234"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          {addError && <p className="text-sm text-red-600 w-full">{addError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <GuardTable
          guards={filtered}
          activeGuardIds={activeGuardIds}
          loading={loading}
          onDeactivate={handleDeactivate}
          onResetPin={handleResetPin}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}
